import axios, { AxiosInstance, AxiosError } from 'axios';
import {
  UserRegistration,
  UserLogin,
  LoginResponse,
  User,
  Event,
  EventWithUUID,
  EventWithShortCode,
  Invite,
  InviteCreate,
  InviteWithUUID,
  InviteWithShortcode,
  InviteListItem,
  InviteWithGuests,
  Guest,
  GuestWithUUID,
  RSVP,
  FrontendInvite,
} from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add token to requests if available
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle 401 errors globally
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: UserLogin): Promise<LoginResponse> {
    const response = await this.api.post<LoginResponse>('/auth', credentials);
    return response.data;
  }

  async register(userData: UserRegistration): Promise<User> {
    const response = await this.api.post<User>('/users', userData);
    return response.data;
  }

  // Event endpoints
  async createEvent(userUuid: string, event: Event): Promise<EventWithUUID> {
    const response = await this.api.post<EventWithUUID>(
      `/users/${userUuid}/events`,
      event
    );
    return response.data;
  }

  async listEvents(userUuid: string): Promise<EventWithShortCode[]> {
    const response = await this.api.get<EventWithShortCode[]>(
      `/users/${userUuid}/events`
    );
    return response.data;
  }

  async getEvent(userUuid: string, eventUuid: string): Promise<EventWithShortCode> {
    const response = await this.api.get<EventWithShortCode>(
      `/users/${userUuid}/events/${eventUuid}`
    );
    return response.data;
  }

  async updateEvent(
    userUuid: string,
    eventUuid: string,
    event: Event
  ): Promise<EventWithUUID> {
    const response = await this.api.put<EventWithUUID>(
      `/users/${userUuid}/events/${eventUuid}`,
      event
    );
    return response.data;
  }

  async deleteEvent(userUuid: string, eventUuid: string): Promise<void> {
    await this.api.delete(`/users/${userUuid}/events/${eventUuid}`);
  }

  // Invite endpoints
  async createInvite(
    userUuid: string,
    eventUuid: string,
    invite: InviteCreate
  ): Promise<InviteWithShortcode | InviteWithGuests> {
    const response = await this.api.post<InviteWithShortcode | InviteWithGuests>(
      `/users/${userUuid}/events/${eventUuid}/invites`,
      invite
    );
    return response.data;
  }

  async listInvites(
    userUuid: string,
    eventUuid: string
  ): Promise<InviteListItem[]> {
    const response = await this.api.get<InviteListItem[]>(
      `/users/${userUuid}/events/${eventUuid}/invites`
    );
    return response.data;
  }

  async getInvite(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string
  ): Promise<InviteWithShortcode> {
    const response = await this.api.get<InviteWithShortcode>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}`
    );
    return response.data;
  }

  async updateInvite(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string,
    invite: Invite
  ): Promise<InviteWithUUID> {
    const response = await this.api.put<InviteWithUUID>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}`,
      invite
    );
    return response.data;
  }

  async deleteInvite(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string
  ): Promise<void> {
    await this.api.delete(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}`
    );
  }

  // Guest endpoints (now nested under invites)
  async createGuest(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string,
    guest: Guest
  ): Promise<GuestWithUUID> {
    const response = await this.api.post<GuestWithUUID>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}/guests`,
      guest
    );
    return response.data;
  }

  async listGuests(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string
  ): Promise<GuestWithUUID[]> {
    const response = await this.api.get<GuestWithUUID[]>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}/guests`
    );
    return response.data;
  }

  async getGuest(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string,
    guestUuid: string
  ): Promise<GuestWithUUID> {
    const response = await this.api.get<GuestWithUUID>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}/guests/${guestUuid}`
    );
    return response.data;
  }

  async updateGuest(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string,
    guestUuid: string,
    guest: Guest
  ): Promise<GuestWithUUID> {
    const response = await this.api.put<GuestWithUUID>(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}/guests/${guestUuid}`,
      guest
    );
    return response.data;
  }

  async deleteGuest(
    userUuid: string,
    eventUuid: string,
    inviteUuid: string,
    guestUuid: string
  ): Promise<void> {
    await this.api.delete(
      `/users/${userUuid}/events/${eventUuid}/invites/${inviteUuid}/guests/${guestUuid}`
    );
  }

  // RSVP endpoints (public)
  async rsvpInvite(eventUuid: string, inviteUuid: string, rsvp: RSVP): Promise<void> {
    await this.api.post(`/events/${eventUuid}/invites/${inviteUuid}/rsvp`, rsvp);
  }

  // Frontend utility endpoints
  async getEventByShortcode(eventShortcode: string): Promise<EventWithUUID> {
    const response = await this.api.get<EventWithUUID>(
      `/event-shortcode-lookup`,
      {
        params: {
          event: eventShortcode,
        },
      }
    );
    return response.data;
  }

  async getInviteByShortcode(
    eventShortcode: string,
    inviteShortcode: string
  ): Promise<FrontendInvite> {
    const response = await this.api.get<FrontendInvite>(
      `/invite-shortcode-lookup`,
      {
        params: {
          event: eventShortcode,
          invite: inviteShortcode,
        },
      }
    );
    return response.data;
  }
}

const api = new ApiService();

export default api;
